import _ from 'lodash';
import { Modal } from 'react-bootstrap';
import Humanize from 'humanize-plus';
import { useState } from 'react';
import { SmartInput } from '../reusable-elements/floating-smart-input';
import { DocTypeSelector } from '../reusable-elements/doctype-selector';
import { notNullOrEmpty, validateField } from "../../utils/validators";
import { getFileFormat } from '../../api/docs/doc-functions';
import { RightAlignedButtons } from '../reusable-elements/right-aligned-buttons';
import DateTimePicker from '../calendar/datetime-picker';
import { arrayAny } from '../../utils/arrayUtils';

export const DocUploadModal = ({ show, handleCancel, handleSave, setFiles, uploadProgress, totalSize, docTypes }) => {

    // TODO: Sync Fusion Word Thumbnail generator license key not properly added to azure config
    const newDoc = () => {
        return {
            path: "",
            title: "",
            description: "",
            documentType: { description: "" }
        }
    };

    const [state, setState] = useState(newDoc());
    const [validationErrors, setValidationErrors] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [openDtp, setOpenDtp] = useState(false);
    const [rerender, setRerender] = useState(false);
    const [fileSelected, setFileSelected] = useState(false);

    const validateState = stateToValidate => {
        validateField(() => notNullOrEmpty(stateToValidate.title), () => stateToValidate.title, setValidationErrors);
        validateField(() => notNullOrEmpty(stateToValidate.description), () => stateToValidate.title, setValidationErrors);
    };

    const handleStateUpdates = updateMethod => {

        setState(current => {
            const newState = updateMethod(current);
            validateState(newState);
            setRerender(!rerender);
            return newState;
        });
    };

    const filesSelected = files => {

        setState(currentDoc => {
            const format = getFileFormat(files[0].type, docTypes.formats);
            currentDoc.documentFormat = format;
            setFiles({ doc: currentDoc, files });
            validateState(currentDoc);
            const _fileSelected = files && files.length > 0;
            setFileSelected(_fileSelected);
            validateField(() => _fileSelected, () => _fileSelected, setValidationErrors);
            return currentDoc;
        });
    };

    const canSave = () => !isSaving && fileSelected && Object.keys(validationErrors ?? {}).length === 0;

    const localHandleCancel = () => {
        setState(newDoc());
        setValidationErrors(null);
        setOpenDtp(false);
        setFiles([]);
        handleCancel();
    };

    const localHandleSave = () => {
        if (!isSaving) {

            setIsSaving(true);

            handleSave()
                .then(result => {
                    validateField(() => true, "fileExists", setValidationErrors);
                    setState(newDoc());
                    setValidationErrors(null);
                    setOpenDtp(false);
                    setFiles([]);
                })
                .catch(error => { console.error(error); validateField(() => false, "fileExists", setValidationErrors); })
                .finally(() => { setRerender(!rerender); setIsSaving(false); });
        }
    };

    return show ?
        (
            <Modal
                show={show}
                onHide={handleCancel}
                backdrop="static"
                keyboard={true}
            >
                <Modal.Header closeButton>
                    <Modal.Title>Create Document</Modal.Title>
                </Modal.Header>
                <Modal.Body>

                    <div className='w-100 mb-5px'>
                        <input className='w-100' type="file" onChange={e => { filesSelected(e.target.files); validateField(() => true, "fileExists", setValidationErrors); }} />
                    </div>
                    {validationErrors && arrayAny( Object.keys(validationErrors)) && arrayAny( _.intersection(Object.keys(validationErrors), ["fileExists"])) &&
                        <div className='mt-15px mb-15px text-red'>
                            The file selected would overwrite an existing file used by an existing document. Choose another file, or rename the file you wish to upload.
                        </div>}

                    <DocTypeSelector
                        docTypes={docTypes}
                        selectedDocType={state.documentType}
                        onDocTypeSelect={newValue => { handleStateUpdates(v => { v.documentType = newValue; return v; }) }}
                    />
                    <SmartInput
                        className={"mt-1 w-100"}
                        label={`${!state.documentType.description ? "Enter" : ""} Document Type Description`}
                        text={state.documentType.description}
                        readOnly={true}
                    />
                    <SmartInput
                        className={"mt-1 w-100"}
                        label={`${!state.title ? "Enter" : ""} Document Name`}
                        text={state.title}
                        updateCallback={newValue => { handleStateUpdates(v => { v.title = newValue; return v; }) }}
                        required={true}
                        validatorFn={() => { return notNullOrEmpty(state.title); }}
                    />
                    <SmartInput
                        className={"mt-1 w-100"}
                        label={`${!state.description ? "Enter" : ""} Description / Details`}
                        text={state.description}
                        updateCallback={newValue => { handleStateUpdates(v => { v.description = newValue; return v; }) }}
                        required={true}
                        validatorFn={() => { return notNullOrEmpty(state.description); }}
                    />

                    <DateTimePicker
                        className={"mt-10px w-100"}
                        fetchComplete={true}
                        openDtp={openDtp}
                        showTime={false}
                        dateValue={state.expiry}
                        placeholder={`${!state.expiry ? "Select" : ""} Expiry Date`}
                        stopEditDate={() => setOpenDtp(false)}
                        setDate={newValue => { handleStateUpdates(v => { v.expiry = newValue; return v; }) }}
                        editDate={() => setOpenDtp(true)}
                    />

                    <div className={"mt-15px w-100"}>
                        {!!totalSize && totalSize > 0 && <div>{Humanize.fileSize(totalSize)} to upload.</div>}
                    </div>

                </Modal.Body>
                <Modal.Footer>
                    <div className='row w-100'>
                        <div className='col-xl-7'>
                            {!!uploadProgress && uploadProgress === 0 && <div>Starting upload... please wait</div>}
                            {!!uploadProgress && uploadProgress > 0 && <div className="mt-10px progress-bar bg-indigo progress-bar-striped progress-bar-animated rounded-pill fs-10px fw-bold" style={{ width: uploadProgress * 100 + '%' }}>{`${(uploadProgress * 100).toFixed(2)}%`}</div>}
                        </div>
                        <RightAlignedButtons>
                            <button className='btn btn-default me-5px' onClick={localHandleCancel}>
                                <i className="fa fa-fw fa-x"></i>
                                Close
                            </button>
                            <button disabled={!canSave()} className='btn btn-blue' onClick={localHandleSave}>
                                <i className={isSaving ? 'fa fa-fw fa-arrows-rotate fa-pulse' : "fa fa-fw fa-arrow-up"}></i>
                                Add
                            </button>
                        </RightAlignedButtons>
                    </div>
                </Modal.Footer>
            </Modal>
        ) :
        (<></>);
};